class RecentlyViewedProducts extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.loadProducts();
  }

  async loadProducts() {
    const handles = JSON.parse(localStorage.getItem('recently_viewed') || '[]');
    if (handles.length === 0) {
      this.innerHTML = '<div class="cart-drawer__empty"><p style="text-align:center; padding: 20px;">No recently viewed products</p></div>';
      return;
    }

    this.innerHTML = '<div class="cart-drawer__items" style="opacity: 0.5; padding: 20px; text-align: center;">Loading...</div>';
    
    const itemsContainer = document.createElement('div');
    itemsContainer.className = 'cart-drawer__items';

    const promises = handles.map(handle => 
      fetch(`/products/${handle}.js`)
        .then(res => res.ok ? res.json() : null)
        .catch(e => null)
    );

    const products = await Promise.all(promises);

    products.forEach(product => {
      if (product) {
        const item = this.createProductItem(product);
        if (item) {
          itemsContainer.appendChild(item);
        }
      }
    });
    
    this.innerHTML = '';
    this.appendChild(itemsContainer);
  }

  createProductItem(product) {
    const item = document.createElement('div');
    item.className = 'cart-drawer__item';
    
    const image = product.featured_image || (product.images && product.images[0]);
    const price = product.price;
    // Ensure variantId is a number (Shopify API requires numeric ID)
    const variantId = parseInt(product.variants[0].id, 10);
    const productHandle = product.handle;

    if (!variantId || isNaN(variantId)) {
      console.error('Invalid variant ID:', product.variants[0].id);
      return null;
    }

    const formattedPrice = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'USD',
      minimumFractionDigits: 2
    }).format(price / 100);

    item.innerHTML = `
      <div class="cart-drawer__item-image">
        ${image ? `<img src="${image}" alt="${product.title}" width="200" height="200" loading="lazy">` : ''}
      </div>
      <div class="cart-drawer__item-details">
        <div class="cart-drawer__item-title-wrapper">
          <h3 class="cart-drawer__item-title caption-large">${product.title}</h3>
          <button type="button" class="cart-drawer__add-to-cart-button caption-large" data-add-to-cart="${variantId}" data-product-handle="${productHandle}">
            <span>ADD TO CART</span>
          </button>
        </div>
        <p class="cart-drawer__item-price caption-large">${formattedPrice}</p>
      </div>
    `;

    const btn = item.querySelector('[data-add-to-cart]');
    btn.addEventListener('click', () => this.addToCart(variantId, productHandle, btn));

    return item;
  }

  updateCartCountDisplay(itemCount) {
    // Update elements with data-cart-count attribute
    document.querySelectorAll('[data-cart-count]').forEach(el => el.textContent = itemCount);
    
    // Update cart header button text
    document.querySelectorAll('[data-cart-header]').forEach(el => el.textContent = `Cart (${itemCount})`);
    
    // Update header cart count spans (desktop and mobile)
    document.querySelectorAll('.header__icons span, .header__mobile-cart span').forEach(el => {
      el.textContent = itemCount;
    });
    
    // Also call the global updateCartCount function if available
    if (typeof updateCartCount === 'function') {
      updateCartCount(itemCount);
    }
  }

  setLoadingState(button, isLoading) {
    if (!button) return;
    
    const span = button.querySelector('span');
    if (isLoading) {
      button.disabled = true;
      button.classList.add('loading');
      if (span) span.textContent = 'Adding...';
    } else {
      button.disabled = false;
      button.classList.remove('loading');
      if (span) span.textContent = 'ADD TO CART';
    }
  }

  async addToCart(variantId, productHandle, button) {
    // Show loading state
    this.setLoadingState(button, true);

    try {
      // Ensure variantId is a number (Shopify API requires numeric ID)
      const numericVariantId = parseInt(variantId, 10);
      if (!numericVariantId || isNaN(numericVariantId)) {
        throw new Error('Invalid variant ID');
      }

      // Use bundled section rendering to get cart drawer content in the same request
      // According to Shopify docs, items array must have numeric id
      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          items: [{ id: numericVariantId, quantity: 1 }],
          sections: 'cart-drawer-content'
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.description || 'Failed to add to cart');
      }

      const responseData = await response.json();
      
      // Remove product from recently viewed localStorage
      if (productHandle) {
        let viewed = JSON.parse(localStorage.getItem('recently_viewed') || '[]');
        viewed = viewed.filter(h => h !== productHandle);
        localStorage.setItem('recently_viewed', JSON.stringify(viewed));
      }

      // Fetch full cart object to get item_count and other cart data
      const cartRes = await fetch(window.Shopify.routes.root + 'cart.js');
      const cart = await cartRes.json();

      // Update cart drawer content using bundled section rendering
      if (responseData.sections && responseData.sections['cart-drawer-content']) {
        // Use the existing cart drawer update function if available
        if (typeof updateCartDrawerContent === 'function') {
          await updateCartDrawerContent(responseData.sections['cart-drawer-content'], cart);
          // Ensure header cart count is updated (updateCartDrawerContent may not update header spans)
          this.updateCartCountDisplay(cart.item_count);
        } else {
          // Fallback: manual update
          const cartDrawerBody = document.getElementById('cart-drawer-body');
          if (cartDrawerBody) {
            const parser = new DOMParser();
            const doc = parser.parseFromString(responseData.sections['cart-drawer-content'], 'text/html');
            const sectionWrapper = doc.querySelector('#cart-drawer-body');
            if (sectionWrapper) {
              cartDrawerBody.innerHTML = sectionWrapper.innerHTML;
            }
          }
          
          // Update cart count - target both data attributes and header spans
          this.updateCartCountDisplay(cart.item_count);
        }
      } else {
        // Fallback: refresh cart drawer if bundled sections not available
        if (typeof refreshCartDrawer === 'function') {
          await refreshCartDrawer(cart);
        } else {
          // Manual refresh
          this.updateCartCountDisplay(cart.item_count);
        }
      }
      
      // Reload recently viewed products to remove the added item
      this.loadProducts();
      
      // Switch to cart tab
      const tabs = document.querySelector('cart-tabs');
      if (tabs) tabs.switchTab('cart');
      
      // Reset loading state after success
      this.setLoadingState(button, false);

      // Dispatch cart update event
      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { cart }
      }));
    } catch (e) {
      console.error('Error adding to cart from recently viewed:', e);
      // Reset loading state on error
      this.setLoadingState(button, false);
      // You could show an error message to the user here
    }
  }
}

customElements.define('recently-viewed-products', RecentlyViewedProducts);
